import { lazy, Suspense } from 'react'
import { Redirect, Route, RouteProps, Switch } from 'react-router-dom'

import { useCurrentUser } from './api/auth/queries/useCurrentUser'
import { Spinner } from './components/ui/spinner'
import ScrollToTop from './helpers/ScrollToTop'
import ClientPage from './pages/client-side/ClientPage'
import ClientPkgPage from './pages/client-side/ClientPkg/ClientPkgsPage'
import ForgotPasswordPage from './pages/client-side/Login/ForgotPasswordPage'
import LoginPage from './pages/client-side/Login/LoginPage'
import MyAddressesPage from './pages/client-side/MyAddresses/MyAddresses'
import PaymentPage from './pages/client-side/Payment/PaymentPage'
import ProfilePage from './pages/client-side/Profile/ProfilePage'
import ReceiptUploadPageV2 from './pages/client-side/ReceiptUpload/ReceiptUploadPage'
import ReturnPkgPage from './pages/client-side/ReturnPkg/ReturnPkgPage'
import UnitePkgPage from './pages/client-side/UnitePkg/UnitePkgPage'
import { RoutePath } from './RoutePath'

const AddNewPackagePage = lazy(() => import('./pages/office-side/AddNewPackage/AddNewPackagePage'))
const AffiliatePage = lazy(() => import('./pages/office-side/Affiliate/AffiliatePage'))
const BarcodeGunShip = lazy(() => import('./pages/office-side/BarcodeGunShip/BarcodeGunShip'))
const CouponOfficePage = lazy(() => import('./pages/office-side/Coupon/CouponOfficePage'))
const DelayedPayments = lazy(() => import('./pages/office-side/DelayedPayments/DelayedPayments'))
const EditAndUpdatePage = lazy(() => import('./pages/office-side/EditAndUpdate/EditAndUpdatePage'))
const Location = lazy(() => import('./pages/office-side/Location/Location'))
const PkgShippingChecklist = lazy(() => import('./pages/office-side/PackageChecklist/PkgShippingChecklist'))
const PackageRequests = lazy(() => import('./pages/office-side/PackageRequests/PackageRequests'))
const PrintPkgPage = lazy(() => import('./pages/office-side/Print/PrintPkgPage'))
const ReceiveShipments = lazy(() => import('./pages/office-side/RecieveShipments/ReceiveShipments'))
const OfficeShipPageComponent = lazy(() => import('./pages/office-side/Ship/OfficeShipPageComponent'))
const Stats = lazy(() => import('./pages/office-side/Stats/Stats'))
const Sticker = lazy(() => import('./pages/office-side/Sticker/Sticker'))
const WarehouseArrivalsPage = lazy(() => import('./pages/office-side/WarehouseArrivals/WarehouseArrivalsPage'))

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RouteWrapper = ({ Component, ...props }: { Component: React.LazyExoticComponent<React.ComponentType<any>> }) => {
  return (
    <Suspense
      fallback={
        <div className='flex items-center justify-center' style={{ height: '100vh' }}>
          <Spinner className='size-12' />
        </div>
      }
    >
      <Component {...props} />
    </Suspense>
  )
}

const ProtectedRoute = ({ children, ...props }: RouteProps) => {
  const { data: loggedUser } = useCurrentUser()
  return <Route {...props}>{loggedUser?.Username ? children : <LoginPage />}</Route>
}

export default function Layout() {
  const { data: loggedUser } = useCurrentUser()

  return (
    <div className='h-[calc(100dvh-var(--navbar-height))]'>
      <ScrollToTop />

      <Switch>
        <Route path='/welcome'>
          <LoginPage />
        </Route>

        <Route path={RoutePath.Login}>
          <LoginPage />
        </Route>

        <Route path={RoutePath.ForgotPassword}>
          <ForgotPasswordPage />
        </Route>

        <ProtectedRoute path={RoutePath.Client}>
          <ClientPage>
            <Route exact path={RoutePath.ClientProfile}>
              <ProfilePage />
            </Route>
            <Route exact path={RoutePath.ClientProfilePersonal}>
              <ProfilePage />
            </Route>
            <Route exact path={RoutePath.ClientProfileAddresses}>
              <ProfilePage />
            </Route>
            <Route exact path={RoutePath.ClientProfileAffiliate}>
              <ProfilePage />
            </Route>
            <Route exact path={RoutePath.ClientAddresses}>
              <MyAddressesPage />
            </Route>
            <Route path={`${RoutePath.ClientPkgReturn}/:pkgId`}>
              <ReturnPkgPage />
            </Route>
            <Route exact path={RoutePath.ClientPkg}>
              <ClientPkgPage />
            </Route>
            <Route exact path={RoutePath.ClientPkgUnite}>
              <UnitePkgPage />
            </Route>
          </ClientPage>
        </ProtectedRoute>

        <ProtectedRoute path={`${RoutePath.ReceiptUpload}/:pkgId`}>
          <ReceiptUploadPageV2 />
        </ProtectedRoute>

        <ProtectedRoute path={`${RoutePath.PaymentShipping}/:pkgId`}>
          <PaymentPage />
        </ProtectedRoute>

        <ProtectedRoute path={`${RoutePath.PaymentSummary}/:pkgId`}>
          <PaymentPage />
        </ProtectedRoute>

        <ProtectedRoute path={`${RoutePath.PaymentProcess}/:pkgId`}>
          <PaymentPage />
        </ProtectedRoute>

        <ProtectedRoute path='/office'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
              <RouteWrapper Component={AddNewPackagePage} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-ship'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
              <RouteWrapper Component={OfficeShipPageComponent} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-e&u'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] ||
              loggedUser.permissions['Agent'] ||
              loggedUser.permissions['OfficeManager'] ||
              loggedUser.permissions['SeniorAgent'] ||
              loggedUser.permissions['Worker']) && <RouteWrapper Component={EditAndUpdatePage} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-packageRequests/:reqType?/:reqId?'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] ||
              loggedUser.permissions['SeniorAgent'] ||
              loggedUser.permissions['Agent'] ||
              loggedUser.permissions['Worker'] ||
              loggedUser.permissions['OfficeManager']) && <RouteWrapper Component={PackageRequests} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-print'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] ||
              loggedUser.permissions['Agent'] ||
              loggedUser.permissions['SeniorAgent'] ||
              loggedUser.permissions['Worker'] ||
              loggedUser.permissions['OfficeManager']) && <RouteWrapper Component={PrintPkgPage} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-checkForm'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
              <RouteWrapper Component={PkgShippingChecklist} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-stats'>
          {loggedUser && (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager']) && <RouteWrapper Component={Stats} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-affiliate'>
          {loggedUser && loggedUser.permissions['Admin'] && <RouteWrapper Component={AffiliatePage} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-coupon'>
          {loggedUser && loggedUser.permissions['Admin'] && <RouteWrapper Component={CouponOfficePage} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-receive-shipments'>
          {loggedUser && (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker']) && (
            <RouteWrapper Component={ReceiveShipments} />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-delayed-payments'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
              <RouteWrapper Component={DelayedPayments} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-ship-barcode-gun'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
              <RouteWrapper Component={BarcodeGunShip} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-location'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
              <RouteWrapper Component={Location} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-warehouse-arrivals'>
          {loggedUser && (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager']) && (
            <RouteWrapper Component={WarehouseArrivalsPage} />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-sticker/:pId/:name/:unit/:brand'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
              <RouteWrapper Component={Sticker} />
            )}
        </ProtectedRoute>

        <Route exact path='/'>
          <LoginPage />
        </Route>
        <Route path='/personal-info'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/receipt-upload'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/package-details'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/package-return-label'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/payment'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/payment-successful'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/rp-payment-successful'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/return-package'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
      </Switch>
    </div>
  )
}
